import React from 'react';
import { ThemeState } from '../../types/chatbot.types';
import { TextField } from '../../../ui/Field';

interface ThemeColorSettingsProps {
  theme: ThemeState;
  onUpdate: (key: keyof ThemeState, value: string) => void;
}

const ThemeColorSettings: React.FC<ThemeColorSettingsProps> = ({ theme, onUpdate }) => {
  return (
    <div className="space-y-6">
      {/* Message Colors */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-agentic-text-primary">
              Message Colors
            </h4>
            <p className="text-xs text-agentic-text-secondary">
              Control how user and bot messages look inside the chat.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ColorInput
            label="User Message Background"
            value={theme.userMsgBg}
            onChange={(val) => onUpdate('userMsgBg', val)}
          />
          <ColorInput
            label="User Message Text Color"
            value={theme.userMsgFont}
            onChange={(val) => onUpdate('userMsgFont', val)}
          />
          <ColorInput
            label="Bot Message Background"
            value={theme.botMsgBg}
            onChange={(val) => onUpdate('botMsgBg', val)}
          />
          <ColorInput
            label="Bot Message Text Color"
            value={theme.botMsgFont}
            onChange={(val) => onUpdate('botMsgFont', val)}
          />
        </div>
      </section>

      {/* Layout & Accent */}
      <section className="space-y-3 border-t pt-4 border-gray-300">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-agentic-text-primary">
              Layout & Accent
            </h4>
            <p className="text-xs text-agentic-text-secondary">
              Customize the chat background, header and primary action button.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ColorInput
            label="Chat Window Background Color"
            value={theme.bg}
            onChange={(val) => onUpdate('bg', val)}
          />
          <ColorInput
            label="Header Background Color"
            value={theme.headerBg}
            onChange={(val) => onUpdate('headerBg', val)}
          />
        </div>

        {/* new header title color control */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ColorInput
            label="Header Title Color"
            value={(theme as any).headerTextFont ?? ''}
            onChange={(val) => onUpdate('headerTextFont' as keyof ThemeState, val)}
          />
          <ColorInput
            label="Send Button Color"
            value={theme.sendBtn}
            onChange={(val) => onUpdate('sendBtn', val)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ColorInput
            label="Input Background"
            value={theme.inputTextBg}
            onChange={(val) => onUpdate('inputTextBg', val)}
          />
          <ColorInput
            label="Input Text Color"
            value={theme.inputTextFont}
            onChange={(val) => onUpdate('inputTextFont', val)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <ColorInput
              label="Input Border Color"
              value={theme.inputBorder}
              onChange={(val) => onUpdate('inputBorder', val)}
            />
            <p className="mt-1 text-[11px] text-agentic-text-secondary">
              Use transparent (e.g. <code className="font-mono">#00000000</code>) to hide the border.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

const ColorInput: React.FC<{
  label: string;
  value: string;
  onChange: (val: string) => void;
}> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-agentic-text-primary mb-1">
      {label}
    </label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-8 border rounded"
      />
      <TextField tone="default-focus-primary"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-3 py-2 text-sm rounded-lg border focus:ring-2"
      />
    </div>
  </div>
);

export default ThemeColorSettings;
